import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FlexContainer, Inputs, Spinner } from '@taxdown/piccolo-web';
import queryString from 'query-string';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { routeStateSelector, searchSelector } from 'Store/modules/router/selectors';
import useTranslation from 'Hooks/useTranslation';
import { useGetUserCustomerData } from 'Containers/Datagathering/views/DgTypeForm/hooks';
import { useAutoRequestPreviousYear } from 'Containers/PreviousYears/hooks';
import { useGetUserWithPreFiling } from 'Containers/Dashboard/hooks';
import FlowColLayout from 'Components/Layouts/FlowColLayout';
import CustomTransition from 'Components/CustomTransition';
import { routes } from 'Constants/routes';
import { FRONT_PLANS, PLAN_FLEX_LITE, plansNameMapping } from 'Constants/plans';
import * as Styled from './styles';
import { INITIAL_STEP, sectionsType } from './options';
import { useFetchFilingUser } from './hooks';
import { getIsAllowedStage } from './utils';

const Filing = (): JSX.Element => {
  useAutoRequestPreviousYear();
  const [step, setStep] = useState(INITIAL_STEP);
  const planes = useSelector(searchSelector);
  const info = useSelector(routeStateSelector);
  const infoPlans = queryString.parse(planes);
  const userStage = useSelector(userStageSelector);
  const { t } = useTranslation(locizeNS.FILING);
  const [compensation, setCompensation] = useState<boolean>(false);
  const { isLoading, data, refetch } = useFetchFilingUser();
  const customerInfo = useGetUserCustomerData();
  const isUserWithPreFiling = useGetUserWithPreFiling(userStage, info);

  const plan = useMemo(
    () =>
      customerInfo && customerInfo.activePlan
        ? customerInfo.activePlan.name === PLAN_FLEX_LITE
          ? FRONT_PLANS.FLEX_LITE
          : plansNameMapping(customerInfo.activePlan.name)
        : FRONT_PLANS.BASIC,
    [customerInfo]
  );

  useEffect(() => {
    const isFlexUser =
      plan === FRONT_PLANS.FLEX ||
      plan === FRONT_PLANS.FLEX_LITE ||
      infoPlans.plans === FRONT_PLANS.FLEX ||
      infoPlans.plans === FRONT_PLANS.FLEX_LITE;
    if (info?.compensation && isFlexUser && !info?.fromFedUser) {
      setStep(7);
    }
  }, [info, infoPlans.plans, plan]);

  useEffect(() => {
    const planInfo = plan !== FRONT_PLANS.BASIC ? plan : infoPlans.plans;
    if (
      (!getIsAllowedStage(step, userStage) &&
        planInfo &&
        planInfo !== FRONT_PLANS.FLEX &&
        planInfo !== FRONT_PLANS.FLEX_LITE) ||
      (isUserWithPreFiling && !info?.isExtendedFlexLite && !info?.isExtendedAndIsToReturn) ||
      (plan === FRONT_PLANS.BASIC && !infoPlans.plans)
    ) {
      history.push(routes.DASHBOARD);
    }
  }, [plan, isUserWithPreFiling]);

  useEffect(() => {
    if (info?.isExtendedFlexLite && !info?.isExtendedAndIsToReturn) {
      setStep(7);
    }
  }, [info]);

  const handleOnSetStep = (val: number): void => {
    setStep(val);
  };

  const wizardOptions = [
    { label: t('filing:Step.one', { context: `${info?.taxReturnType}` }), value: 1 },
    { label: t('filing:Step.two', { context: `${info?.taxReturnType}` }), value: 2 },
    { label: t('filing:Step.three', { context: `${info?.taxReturnType}` }), value: 3 },
  ];

  const handlePrevStep = (): void => {
    setStep(step - 1);
  };

  const sections = sectionsType.find(item => item.id === step);
  const { Component } = sections || sectionsType[step];
  return (
    <FlowColLayout section="filing" isFlow>
      <CustomTransition start>
        <Styled.Container direction="column" fullHeight align="center">
          <Styled.WizardBox>
            <Inputs.Wizard options={wizardOptions} value={step} />
          </Styled.WizardBox>
          <Styled.Box direction="column" fullHeight>
            {isLoading || !data ? (
              <FlexContainer justify="center" align="center">
                <Spinner size="M" />
              </FlexContainer>
            ) : (
              <Component
                onChangeStep={handleOnSetStep}
                handlePrevStep={handlePrevStep}
                compensation={compensation}
                setCompensation={setCompensation}
                onRefetchFilingUser={refetch}
                isAllowedStage={getIsAllowedStage(step, userStage)}
              />
            )}
          </Styled.Box>
        </Styled.Container>
      </CustomTransition>
    </FlowColLayout>
  );
};

export default Filing;
